import React, { Component, Context } from "react";
import { connect } from "react-redux";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import AppContext, { IAppContext } from "../contexts/app/AppContext";
// import CircularProgress from "@material-ui/core/CircularProgress"; // TODO
import NotificationsService from "../laco-common/services/notifications";
import SitePagesPoller from "../services/SitePagesPoller";
import AppStore from "./ecwid/StoreContext";
import { logMethod } from "../utils/logger";
import { IAppState, mapAppStateToProps } from "../redux/reducers/app";
import { IAppActionDispatches, mapAppDispatchToProps } from "../redux/actions/app";
// import { ProtectedRoute } from "./auth";

// pages
import SitePage from "./pages/SitePage";
import FormPage from "./siteForms/FormPage";
import GalleryPage from "./gallery/GalleryPage";
import AuthPage from "./auth/pages/AuthPage";
import PeerSupport from "./peer-support";

// ui
import { withAuth, IAuthProps } from "../laco-common/auth/AuthContext";
import Header from "./layout/Header";
import Admin from "../admin";
import LoadStreams from "./pages/LoadStreams";
import { UISnackbars } from "./notifications/component";
import UILoader from "./ui/UILoader";
import { IPage } from "../laco-common/base/models/page";
import { AppMediaOverlay } from "./ui/Media/AppMediaOverlay";
import Footer from "./layout/Footer";
import LayoutProvider from "../contexts/layout/LayoutProvider";
import UpdateProfile from "./auth/pages/UpdateProfile";
import { WidgetType } from "../laco-common/base/models/widget";

// Auth routes
// maybe you want to keep the provider. 1 full rerender
// you will have AuthProtected routes. So they will be loading and only resolved on that state change

const supportsHistory = "pushState" in window.history;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const stripeOptions = {
	// https://stripe.com/docs/js/elements_object/create#stripe_elements-options
	// passing the client secret obtained from the server
	// clientSecret: process.env.REACT_APP_STRIPE_SECRET_KEY,
};

export function PublicRoute({ component: Component, componentProps = {}, ...rest }) {
	return (
		<Route
			{...rest}
			render={(props) => {
				return <Component {...props} {...componentProps} />;
			}}
		/>
	);
}

export function ProtectedRoute({ component: Component, loading, authenticated, componentProps = {}, ...rest }) {
	return (
		<Route
			{...rest}
			render={(props) =>
				loading === true ? (
					<UILoader display={true} />
				) : authenticated === true ? (
					<Component {...props} {...componentProps} />
				) : (
					<Redirect to={{ pathname: "/login", state: { redirectedFrom: props.location } }} />
				)
			}
		/>
	);
}

export function RouteRequiresAuthResolution({
	component: Component,
	loading,
	authenticated,
	componentProps = {},
	...rest
}) {
	return (
		<Route
			{...rest}
			render={(props) =>
				loading === true ? <UILoader display={true} /> : <Component {...props} {...componentProps} />
			}
		/>
	);
}

export type AppProps = {
	store;
};

class App extends Component<AppProps & IAuthProps & IAppState & IAppActionDispatches> {
	static contextType: Context<IAppContext> = AppContext;
	private prevLocation: string;

	@logMethod
	componentDidMount() {
		console.log("App componentDidMount() authContext", this.props.authContext);
		SitePagesPoller.store = this.props.store;
		SitePagesPoller.startPolling("site", undefined); // { pages: this.props.pages, navigation: this.props.navigation }
		AppStore.reduxStore = this.props.store;
		this.props.loadSiteConfiguration();
		this.props.loadTextContent();
		this.props.loadQuotes();
		this.props.loadCustomWidgets();
	}

	// shouldComponentUpdate(nextProps, nextState): boolean {
	// 	return false;
	// }

	// if from same parent component path
	currentRouteSharesPathWithPrev(pathname: string): boolean {
		let isFromSamePath: boolean = false;
		if (this.prevLocation && this.prevLocation !== "/") {
			// so far keep checking
			let pathFragments: string[] = pathname.split("/");
			let prevFragments: string[] = this.prevLocation.split("/");
			if (pathFragments.length >= 2 && prevFragments.length >= 2) {
				if (pathFragments[1] == prevFragments[1]) {
					isFromSamePath = true;
				}
			}
		}

		this.prevLocation = pathname; // save pathname for next comparison
		return isFromSamePath;
	}

	loadContentRoutes() {
		return (
			// switchProps only passes children and location
			<Route
				render={(props: any) => {
					const { location } = props;
					const { pathname } = location;
					let renderWithTransition: boolean = false;
					if (pathname.startsWith("/store") || this.currentRouteSharesPathWithPrev(pathname)) {
						renderWithTransition = false;
					}

					if (!renderWithTransition) {
						return this.renderRoutes();
					}
					return (
						<TransitionGroup>
							<CSSTransition
								// key={pathname}
								classNames="page"
								transitionEnterTimeout={100}
								transitionLeaveTimeout={100}
								timeout={{
									enter: 100,
									exit: 100,
								}}
							>
								<Route location={location} render={(props) => this.renderRoutes()} />
							</CSSTransition>
						</TransitionGroup>
					);
				}}
			/>
		);
	}

	renderRoutes() {
		const { loading, isAuthenticated } = this.props.authContext;
		const renderedRoutesMap = {
			"user-area": true,
			"user/profile": true,
			// checkout: true,
		};

		return (
			<Switch>
				{/* <PublicRoute path="/" exact component={Home} />
				<PublicRoute path="/events" exact component={EventsCalendar} />
				<PublicRoute path="/streams" exact component={Streams} /> */}
				<ProtectedRoute
					path="/user/profile"
					component={SitePage}
					componentProps={{
						page: {
							id: "userProfile",
							path: "user/profile",
						},
						widgets: [{ id: "user-profile", component: UpdateProfile }],
						mergePageProps: true,
					}}
					loading={loading}
					authenticated={isAuthenticated}
				/>
				<ProtectedRoute path="/admin" component={Admin} loading={loading} authenticated={isAuthenticated} />
				{/* <ProtectedRoute
					path="/checkout"
					component={SitePage}
					loading={loading}
					authenticated={isAuthenticated}
					componentProps={{
						page: {
							id: "checkout",
							path: "checkout",
						},
						widgets: [{ id: "checkout", component: SubscriptionCheckout }],
						mergePageProps: true,
					}}
				/> */}

				<PublicRoute
					path="/login"
					exact
					component={AuthPage}
					componentProps={{ pageName: "login", pageId: "login" }}
				/>
				<PublicRoute
					path="/join"
					exact
					component={AuthPage}
					componentProps={{ pageName: "join", pageId: "join" }}
				/>
				<PublicRoute
					path="/forgot-password"
					exact
					component={AuthPage}
					componentProps={{ pageName: "forgot-password", pageId: "forgot-password" }}
				/>
				<PublicRoute path="/gallery/:id" exact component={GalleryPage} />
				<PublicRoute path="/ps" component={PeerSupport} componentProps={{ isNested: true }} />

				{/* <PublicRoute
					path="/form/:id"
					exact
					component={FormPage}
					// componentProps={{ formId: "medicalEquipment" }}
				/> */}

				{/* {this.renderSiteRoutes()} */}
				{/* <UserActionRoute loading={this.state.loadingUserData} userDataLoaded={this.state.userDataLoaded} exact={true} path="/user/pages/:id" component={ManageEntity} /> */}

				<Route
					render={() => {
						if (this.props.pages.length === 0) {
							return <UILoader display={true} />;
						}
						return (
							<Switch>
								{this.renderInternalSiteRoutes()}
								{this.renderSiteRoutes({ loading, authenticated: isAuthenticated })}
								<Route
									render={() => {
										NotificationsService.warning("Page not found.", undefined, {
											autoHideDuration: 110000,
										});
										return <Redirect to="/" />;
									}}
								/>
							</Switch>
						);
					}}
				/>
			</Switch>
		);
	}

	hasIdPage(p: IPage): boolean {
		return (
			p.id === "events" ||
			p.id === "forms" ||
			p.id === "streams" ||
			p.id === "marketplace" ||
			p.id === "page-messages" ||
			(!!p.widgets?.length && !!p.widgets.find((w: WidgetType) => w === "streams"))
		);
	}

	renderSiteRoutes(authProps: { loading: boolean; authenticated: boolean }) {
		return this.props.pages.map((p: IPage) => {
			// console.log("page:", p);
			// if (!renderedRoutesMap[p.path])
			const idPage: boolean =
				p.path?.indexOf("events") > 0 ||
				p.path?.indexOf("marketplace") > 0 ||
				p.path?.indexOf("messages") > 0 ||
				p.path?.indexOf("forms") > 0 ||
				p.path?.indexOf("straight-streams") > 0 ||
				p.path?.indexOf("checkout") > 0;

			if (p.requiresAuth) {
				return (
					<ProtectedRoute
						path={`/${p.path}`}
						key={p.id}
						exact={!idPage} // TODO
						component={SitePage}
						componentProps={{ page: p, idPage }}
						{...authProps}
					/>
				);
			} else if (p.requiresAuthResolution) {
				return (
					<RouteRequiresAuthResolution
						path={`/${p.path}`}
						key={p.id}
						exact={!idPage} // TODO
						component={SitePage}
						componentProps={{ page: p, idPage }}
						{...authProps}
					/>
				);
			} else {
				return (
					<PublicRoute
						path={`/${p.path}`}
						key={p.id}
						// exact={!this.hasIdPage(p)}
						exact={!idPage} // TODO
						component={SitePage}
						componentProps={{ page: p, idPage }}
					/>
				);
			}
		});
	}

	renderInternalSiteRoutes() {
		return this.props.pages.map((p: IPage) => {
			if (this.hasIdPage(p)) {
				const page: IPage = { ...p };
				const widgets: any[] = p.id === "forms" ? [{ component: FormPage }] : undefined;
				return (
					<PublicRoute
						path={`/${p.path}/:id`}
						key={`${p.id}-id`}
						exact
						component={SitePage}
						// componentProps={{ page: p, idPage: true }}
						componentProps={{
							page,
							idPage: true,
							widgets,
						}}
					/>
				);
			}
		});
	}

	render() {
		// console.debug("App.render()");

		return (
			<Elements stripe={stripePromise} options={stripeOptions}>
				<LayoutProvider>
					<BrowserRouter forceRefresh={!supportsHistory}>
						<Switch>
							<Route path="/login" />
							<Route path="/join" />
							<Route path="/forgot-password" />
							<Route path="/reset-password" />
							<Route path="/user-area" />
							{/*<Route path="/ps" />*/}
							<Route path="/AuthAction" />
							<Route path="/admin" />
							<Route path="/" render={(props) => <Header />} />
						</Switch>

						{this.renderRoutes()}

						{/* loadContentRoutes */}

						{/* <div>
						<Logo />
						<NavLink to="/">Home</NavLink>
						<NavLink to="/login">Login</NavLink>
						<NavLink to="/events">Events</NavLink>
						<NavLink to="/streams">Streams</NavLink>
						<NavLink to="/user-area">User Area</NavLink>
					</div> */}

						<Switch>
							<Route path="/login" />
							<Route path="/join" />
							<Route path="/forgot-password" />
							<Route path="/reset-password" />
							<Route path="/user-area" />
							{/*<Route path="/ps" />*/}
							<Route path="/AuthAction" />
							<Route path="/admin" />
							<Route
								path="/"
								render={(props) => (
									<Footer
										page={this.props.activePage}
										bottomOnly={this.props.activePage?.id === "page-payment"}
									/>
								)}
							/>
						</Switch>
						<AppMediaOverlay />
					</BrowserRouter>
					<LoadStreams />
					<UISnackbars />
				</LayoutProvider>
			</Elements>
		);
	}
}

export default connect<IAppState, IAppActionDispatches, AppProps>(
	mapAppStateToProps,
	mapAppDispatchToProps
)(withAuth(App));
